import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { LeaderboardCompetitionsApi, ScoringHealthResponse } from "@devour/client";
import getConfig from "@/utils/getConfig";
import { IStore } from "@/redux/defaultStore";

export const SCORING_HEALTH_QUERY_KEY = "leaderboard-scoring-health";

/**
 * "Is my play counting on this board?" — the per-player verdict behind the
 * scoring-health row. Registered players only: the server 404s anyone else,
 * so callers gate `enabled` on registration rather than paying for the 404.
 *
 * Fresh for a minute, then refetched on focus; the websocket
 * (SCORING_HEALTH_CHANGED, COMPETITION_SOURCE_REJECTED) invalidates it the
 * moment the state actually moves.
 */
export function useScoringHealth(leaderboardId: string | undefined, enabled: boolean) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: [SCORING_HEALTH_QUERY_KEY, leaderboardId, fullToken?.id],
        queryFn: async (): Promise<ScoringHealthResponse | null> => {
            const response = await new LeaderboardCompetitionsApi(getConfig(fullToken))
                .getLeaderboardCompetitionsIdOrSlugScoringHealth({ idOrSlug: leaderboardId! });
            return response ?? null;
        },
        staleTime: 60 * 1000,
        refetchOnWindowFocus: true,
        enabled: enabled && !!leaderboardId && !!fullToken?.token,
    });
}
